/**
 * @file src/pages/company-onboarding-wizard/components/Step3ContactInfo.jsx
 * @description Wizard step 3 — optional contact details (phone and email).
 *
 * What changed from the original:
 *  - formatPhoneNumber was broken — it stripped the "+" then re-added it
 *    incorrectly for already-formatted numbers. Replaced with
 *    formatSaudiPhone from utils/formatters.js which handles all
 *    4 common Saudi input patterns correctly.
 *  - Live validation indicators (CheckCircle) now use the same regex
 *    from config/constants — not duplicated inline
 *  - isPhoneValid / isEmailValid replaced with imports from utils/validation
 *  - aria attributes added throughout
 *
 * Rules:
 *  - No API calls, no validation logic
 *  - Errors come from parent via props
 *  - Both fields are optional — never block progression
 */

import React from "react";
import { motion } from "framer-motion";
import { Phone, Mail, ArrowLeft, CheckCircle } from "lucide-react";
import Input from "components/ui/Input";
import Button from "components/ui/Button";
import { formatSaudiPhone } from "utils/formatters";
import { SAUDI_PHONE_REGEX, EMAIL_REGEX } from "config/constants";

// ---------------------------------------------------------------------------
// Step3ContactInfo
// ---------------------------------------------------------------------------

const Step3ContactInfo = ({
  formData,
  updateFormData,
  errors,
  onNext,
  onPrevious,
}) => {
  // ── Phone handler — formats on every keystroke ───────────────────────────
  const handlePhoneChange = (raw) => {
    const formatted = formatSaudiPhone(raw);
    updateFormData("phone", formatted);
  };

  // ── Live validity indicators ─────────────────────────────────────────────
  const phoneValid = formData.phone && SAUDI_PHONE_REGEX.test(formData.phone);
  const emailValid = formData.email && EMAIL_REGEX.test(formData.email);

  return (
    <div className="space-y-6">
      {/* ── Section header ────────────────────────────────────────────── */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-accent-50 px-4 py-2 rounded-full text-accent-700 mb-2">
          <Phone className="w-4 h-4" />
          <span className="text-sm font-medium">Optional Information</span>
        </div>
        <p className="text-gray-600 text-sm">
          Add contact details to help team members reach your company
        </p>
      </div>

      {/* ── Phone ─────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <label
          htmlFor="contact-phone"
          className="block text-sm font-medium text-gray-700"
        >
          Company Phone
          <span className="text-gray-400 ml-1 font-normal">(Optional)</span>
        </label>

        <div className="relative">
          <Input
            id="contact-phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handlePhoneChange(e.target.value)}
            placeholder="+9665XXXXXXXX"
            className={errors.phone ? "border-error" : ""}
            aria-describedby={errors.phone ? "phone-error" : "phone-hint"}
            autoComplete="tel"
          />
          {/* Valid indicator — only shown when format is correct */}
          {phoneValid && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
          )}
        </div>

        {errors.phone ? (
          <p id="phone-error" className="text-sm text-error" role="alert">
            {errors.phone}
          </p>
        ) : (
          <p id="phone-hint" className="text-xs text-gray-500">
            E.164 format — e.g. +9665XXXXXXXX
          </p>
        )}
      </div>

      {/* ── Email ─────────────────────────────────────────────────────── */}
      <div className="space-y-2">
        <label
          htmlFor="contact-email"
          className="block text-sm font-medium text-gray-700"
        >
          Company Email
          <span className="text-gray-400 ml-1 font-normal">(Optional)</span>
        </label>

        <div className="relative">
          <Input
            id="contact-email"
            type="email"
            value={formData.email}
            onChange={(e) => updateFormData("email", e.target.value)}
            placeholder="info@company.sa"
            className={errors.email ? "border-error" : ""}
            aria-describedby={errors.email ? "email-error" : undefined}
            autoComplete="email"
          />
          {/* Valid indicator */}
          {emailValid && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
          )}
        </div>

        {errors.email && (
          <p id="email-error" className="text-sm text-error" role="alert">
            {errors.email}
          </p>
        )}
      </div>

      {/* ── Contact preview — only shown when at least one field is filled ── */}
      {(formData.phone || formData.email) && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gray-50 rounded-lg p-4 border border-gray-200"
        >
          <h4 className="font-medium text-gray-800 mb-2 text-sm">
            Contact Preview
          </h4>
          <div className="space-y-1.5">
            {formData.phone && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                <span>{formData.phone}</span>
              </div>
            )}
            {formData.email && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                <span>{formData.email}</span>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {/* ── Ready to complete banner ──────────────────────────────────── */}
      <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg shrink-0">
            <CheckCircle className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h4 className="font-medium text-primary-800">Ready to Complete</h4>
            <p className="text-sm text-primary-700">
              Click "Complete Setup" to create your company profile.
            </p>
          </div>
        </div>
      </div>

      {/* ── Navigation ───────────────────────────────────────────────── */}
      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onPrevious}
          className="min-w-[120px]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Previous
        </Button>

        <Button
          onClick={onNext}
          className="min-w-[140px] bg-success hover:bg-success-600"
        >
          Complete Setup
          <CheckCircle className="w-4 h-4 ml-2" />
        </Button>
      </div>

      <div className="text-center text-sm text-gray-500">
        Step 3 of 3 • Contact Information
      </div>
    </div>
  );
};

export default Step3ContactInfo;
